import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Diamond, Mail, KeyRound, Smartphone, ShieldCheck, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Bootstrap() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 email → 2 code → 3 credentials → 4 totp → 5 recovery
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [totp, setTotp] = useState({ secret: "", qr: "", uri: "" });
  const [totpCode, setTotpCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/bootstrap/status").then((r) => {
      if (r.data.bootstrap_completed) {
        toast.info("Kurulum daha önce tamamlanmış");
        navigate("/login", { replace: true });
      }
    }).catch(() => {});
  }, [navigate]);

  const startEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bootstrap/start", { email: email.trim().toLowerCase() });
      setSetupToken(data.setup_token);
      setStep(2);
      toast.success("Doğrulama kodu e-postanıza gönderildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İstek başarısız");
    } finally { setBusy(false); }
  };

  const verifyCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bootstrap/verify-email", { setup_token: setupToken, code: code.trim() });
      setSetupToken(data.setup_token);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod hatalı");
    } finally { setBusy(false); }
  };

  const submitCreds = async () => {
    if (password !== password2) return toast.error("Şifreler eşleşmiyor");
    setBusy(true);
    try {
      const { data } = await api.post("/bootstrap/set-credentials", { setup_token: setupToken, name: name.trim(), username: username.trim().toLowerCase(), password });
      setSetupToken(data.setup_token);
      setTotp({ secret: data.totp_secret, qr: data.qr_data_url, uri: data.totp_uri });
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bilgiler kabul edilmedi");
    } finally { setBusy(false); }
  };

  const confirmTotp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bootstrap/verify-totp", { setup_token: setupToken, code: totpCode.trim() });
      setRecovery(data.recovery_codes || []);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod doğrulanamadı");
    } finally { setBusy(false); }
  };

  const copyRecovery = () => {
    const text = recovery.join("\n");
    navigator.clipboard.writeText(text).then(() => { setCopied(true); toast.success("Panoya kopyalandı"); }).catch(() => toast.error("Kopyalanamadı"));
  };

  const strong = (p) => p.length >= 12 && /[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p);

  return (
    <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center p-6">
      <div className="w-full max-w-lg card-lux p-8" data-testid="bootstrap-root">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 mb-2">
            <Diamond className="w-5 h-5" style={{ color: "#c5a059" }} />
            <span className="font-mono-code text-xs tracking-[0.35em] text-stone-500">RIVACO · İLK KURULUM</span>
          </div>
          <h1 className="font-serif-display text-3xl text-stone-900">Owner Hesabı Kurulumu</h1>
          <div className="text-xs text-stone-500 mt-1">Adım {step} / 5</div>
        </div>

        {step === 1 && (
          <div>
            <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-amber-700" /><h2 className="font-medium">1. Owner e-postası</h2></div>
            <p className="text-xs text-stone-500 mb-3">Yalnızca yayına alınmadan önce belirlenen Owner e-postası kabul edilir.</p>
            <input data-testid="bootstrap-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@rivaco.com" className="w-full px-4 py-3 rounded-lg border border-stone-200 outline-none focus:border-amber-500" />
            <button data-testid="bootstrap-email-next" onClick={startEmail} disabled={busy || !email.includes("@")} className="w-full mt-4 py-3 rounded-lg gold-gradient text-white disabled:opacity-60">Doğrulama Kodu Gönder</button>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-amber-700" /><h2 className="font-medium">2. E-posta doğrulama kodu</h2></div>
            <p className="text-xs text-stone-500 mb-3">E-postanıza gönderilen 6 haneli kodu girin.</p>
            <input data-testid="bootstrap-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" className="w-full px-4 py-3 rounded-lg border border-stone-200 tracking-[0.4em] text-center text-lg outline-none focus:border-amber-500" />
            <button data-testid="bootstrap-code-next" onClick={verifyCode} disabled={busy || code.length !== 6} className="w-full mt-4 py-3 rounded-lg gold-gradient text-white disabled:opacity-60">Doğrula</button>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4 text-amber-700" /><h2 className="font-medium">3. Owner bilgileri ve güçlü şifre</h2></div>
            <input data-testid="bootstrap-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad" className="w-full mb-3 px-4 py-3 rounded-lg border border-stone-200 outline-none focus:border-amber-500" />
            <input data-testid="bootstrap-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Kullanıcı adı" className="w-full mb-3 px-4 py-3 rounded-lg border border-stone-200 outline-none focus:border-amber-500" />
            <input data-testid="bootstrap-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Şifre (en az 12 karakter)" className="w-full mb-2 px-4 py-3 rounded-lg border border-stone-200 outline-none focus:border-amber-500" />
            <div className={`text-[11px] mb-2 ${strong(password) ? "text-emerald-700" : "text-stone-500"}`}>{strong(password) ? "✓ Güçlü şifre" : "En az 12 karakter, büyük/küçük harf ve rakam içermelidir"}</div>
            <input data-testid="bootstrap-password2" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} placeholder="Şifreyi tekrar girin" className="w-full mb-3 px-4 py-3 rounded-lg border border-stone-200 outline-none focus:border-amber-500" />
            <button data-testid="bootstrap-creds-next" onClick={submitCreds} disabled={busy || !name || !username || !strong(password) || password !== password2} className="w-full py-3 rounded-lg gold-gradient text-white disabled:opacity-60">Devam Et</button>
          </div>
        )}

        {step === 4 && (
          <div>
            <div className="flex items-center gap-2 mb-3"><Smartphone className="w-4 h-4 text-amber-700" /><h2 className="font-medium">4. Authenticator (TOTP) 2FA</h2></div>
            <p className="text-xs text-stone-500 mb-3">Google Authenticator, Microsoft Authenticator veya Authy uygulamasında QR kodu tarayın ve 6 haneli kodu girin.</p>
            {totp.qr && <img data-testid="bootstrap-totp-qr" src={totp.qr} alt="TOTP QR" className="w-48 h-48 mx-auto mb-3 border border-stone-200 rounded-lg" />}
            <div className="text-[11px] text-center text-stone-500 mb-3 font-mono-code break-all">Secret: {totp.secret}</div>
            <input data-testid="bootstrap-totp-code" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6 haneli kod" inputMode="numeric" className="w-full px-4 py-3 rounded-lg border border-stone-200 tracking-[0.4em] text-center text-lg outline-none focus:border-amber-500" />
            <button data-testid="bootstrap-totp-confirm" onClick={confirmTotp} disabled={busy || totpCode.length !== 6} className="w-full mt-4 py-3 rounded-lg gold-gradient text-white disabled:opacity-60">2FA'yı Aktifleştir</button>
          </div>
        )}

        {step === 5 && (
          <div data-testid="bootstrap-recovery">
            <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-emerald-700" /><h2 className="font-medium">5. Kurtarma Kodları</h2></div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 mb-3">
              Aşağıdaki 10 kurtarma kodunu güvenli bir yerde saklayın. Her kod yalnızca <b>bir kez</b> kullanılabilir. Telefonunuzu kaybederseniz bu kodlarla giriş yapabilirsiniz.
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3 font-mono-code text-sm">
              {recovery.map((c, i) => (<div key={i} className="p-2 border border-stone-200 rounded bg-stone-50 text-center">{c}</div>))}
            </div>
            <button onClick={copyRecovery} data-testid="bootstrap-copy-recovery" className="w-full py-2 rounded-lg border border-stone-200 text-sm inline-flex items-center justify-center gap-2 hover:bg-stone-50">
              {copied ? <><Check className="w-4 h-4" /> Kopyalandı</> : <><Copy className="w-4 h-4" /> Kodları Kopyala</>}
            </button>
            <button data-testid="bootstrap-finish" onClick={() => { toast.success("Kurulum tamamlandı"); navigate("/login", { replace: true }); }} className="w-full mt-3 py-3 rounded-lg gold-gradient text-white">Giriş Ekranına Git</button>
          </div>
        )}
      </div>
    </div>
  );
}
